/** Original vector icons for techniques and items (no external assets). */
export function Icon({ name, color = '#ecd08e' }: { name: string; color?: string }) {
  const bg = (
    <defs>
      <radialGradient id={`g-${name}`} cx="50%" cy="40%" r="70%">
        <stop offset="0" stopColor={color} stopOpacity="0.35" />
        <stop offset="1" stopColor="#000" stopOpacity="0" />
      </radialGradient>
    </defs>
  );
  const glow = <rect x="0" y="0" width="64" height="64" fill={`url(#g-${name})`} />;
  switch (name) {
    case 'combo':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          {glow}
          {[0, 1, 2].map((i) => (
            <path
              key={i}
              d={`M${14 + i * 9} ${50 - i * 2} Q ${30 + i * 6} ${30 - i * 4} ${44 + i * 5} ${12 + i * 2}`}
              stroke={color}
              strokeWidth={3.2 - i * 0.6}
              fill="none"
              strokeLinecap="round"
              opacity={1 - i * 0.2}
            />
          ))}
        </svg>
      );
    case 'thrust':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          {glow}
          <path d="M12 52 L46 18" stroke="#e8edf2" strokeWidth="4" strokeLinecap="round" />
          <path d="M46 18 L54 10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M16 42 L22 48" stroke={color} strokeWidth="4" strokeLinecap="round" />
          <circle cx="50" cy="14" r="7" fill="none" stroke={color} strokeWidth="2" opacity="0.8" />
          <path
            d="M50 2 V8 M50 20 V26 M38 14 H44 M56 14 H62"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'crescent':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          {glow}
          <path d="M40 8 A24 24 0 1 0 40 56 A18 18 0 1 1 40 8 Z" fill={color} />
          <path
            d="M6 22 H24 M4 32 H20 M6 42 H24"
            stroke={color}
            strokeWidth="2.4"
            strokeLinecap="round"
            opacity="0.6"
          />
        </svg>
      );
    case 'taiji':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          {glow}
          <circle cx="32" cy="32" r="22" fill="#1b1b21" stroke={color} strokeWidth="2.5" />
          <path d="M32 10 A22 22 0 0 1 32 54 A11 11 0 0 1 32 32 A11 11 0 0 0 32 10 Z" fill={color} />
          <circle cx="32" cy="21" r="3.5" fill="#1b1b21" />
          <circle cx="32" cy="43" r="3.5" fill={color} />
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke={color}
            strokeWidth="1"
            strokeDasharray="4 4"
            opacity="0.6"
          />
        </svg>
      );
    case 'sword':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          {glow}
          <path d="M44 10 L20 42" stroke={color} strokeWidth="4.5" strokeLinecap="round" />
          <path d="M14 38 L26 48" stroke="#b08d3c" strokeWidth="4" strokeLinecap="round" />
          <path d="M18 46 L12 52" stroke="#3a2a1c" strokeWidth="4" strokeLinecap="round" />
          <circle cx="11" cy="53" r="3" fill="#b0302a" />
        </svg>
      );
    case 'robe':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          {glow}
          <path
            d="M22 10 L32 18 L42 10 L54 18 L48 28 L44 26 L46 54 H18 L20 26 L16 28 L10 18 Z"
            fill={color}
            stroke="#16161a"
            strokeWidth="1.5"
          />
          <path d="M32 18 L26 32 M32 18 L38 32" stroke="#16161a" strokeWidth="1.5" />
          <path d="M19 34 H45" stroke="#c9a35a" strokeWidth="3" />
        </svg>
      );
    case 'pill_red':
    case 'pill_blue': {
      const c = name === 'pill_red' ? '#e0574a' : '#4b86c4';
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          <circle cx="32" cy="34" r="15" fill={c} />
          <circle cx="27" cy="29" r="5" fill="#fff" opacity="0.45" />
          <path d="M22 16 L32 8 L42 16" stroke="#c9a35a" strokeWidth="2" fill="none" />
        </svg>
      );
    }
    case 'pelt':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          <path
            d="M12 20 Q20 10 32 14 Q44 10 52 20 Q48 34 54 48 Q42 52 32 46 Q22 52 10 48 Q16 34 12 20Z"
            fill="#8a8986"
            stroke="#4a4846"
            strokeWidth="2"
          />
          <path d="M20 26 Q32 22 44 26" stroke="#5b5753" strokeWidth="2" fill="none" />
        </svg>
      );
    case 'fang':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          <path d="M22 10 Q46 14 44 52 Q36 30 18 20 Z" fill="#efe8d6" stroke="#8a7a5a" strokeWidth="2" />
        </svg>
      );
    case 'sack':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          {bg}
          <path
            d="M24 16 L40 16 L36 22 Q52 30 48 48 Q46 56 32 56 Q18 56 16 48 Q12 30 28 22 Z"
            fill="#c9a96a"
            stroke="#6b5230"
            strokeWidth="2"
          />
          <path d="M26 22 H38" stroke="#9b2c2c" strokeWidth="3" />
          <text x="32" y="44" fontSize="12" textAnchor="middle" fill="#9b2c2c" fontWeight="700">
            米
          </text>
        </svg>
      );
    case 'bite':
    case 'slash':
    case 'chop':
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <path d="M12 52 L52 12" stroke="#e0574a" strokeWidth="4" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="32" r="10" fill="#555" />
        </svg>
      );
  }
}
